import React from 'react';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber } from '@fabric-ui/utils';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import useQueryObject from '../../../../hooks/useQueryObjectSql';
import { DwQueryRequest } from '../../../CustomDashboards/types';
import PercentageChange from '../percentageChange';
import { FilterContext } from '../types';
import { calculatePercentageChange, getLast30DaysDate, getNumericValue } from '../utils';

interface ThreatStatusProps {
  title: string;
  totalFunc: (filterContext: FilterContext) => DwQueryRequest | string;
  filterContext: FilterContext;
  dataConverter?: (data: any[]) => any;
}

const ThreatStatus = (props: ThreatStatusProps) => {
  const { title, filterContext, totalFunc } = props;
  const theme = useTheme();

  const countFromRange = useQueryObject({
    queryObject: props.totalFunc(filterContext) as DwQueryRequest,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => {
      if (props.dataConverter) {
        return props.dataConverter(data);
      }
      return getNumericValue(data?.[0]?.value);
    },
  });

  const last30DaysCount = useQueryObject({
    queryObject: totalFunc({
      ...filterContext,
      dateRange: [getLast30DaysDate(filterContext.dateRange[1]), filterContext.dateRange[1]],
    }) as DwQueryRequest,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => {
      if (props.dataConverter) {
        return props.dataConverter(data);
      }
      return getNumericValue(data?.[0]?.value);
    },
  });

  const percentage = calculatePercentageChange(getNumericValue(last30DaysCount.data), getNumericValue(countFromRange.data));

  return (
    <Box sx={{ padding: '16px 24px', background: `${theme.palette.white.main}`, minHeight: '137px', height: '100%', ...flex.col }}>
      <Typography sx={{ fontSize: '18px', marginBottom: '16px' }} variant="h2">
        {title}
      </Typography>
      {!countFromRange.isLoading && !countFromRange.isRefetching ? (
        <div>
          <Typography variant="h3" sx={{ m: 0 }}>
            {abbreviateNumber(countFromRange?.data)}
          </Typography>
          <Box sx={{ ...flex.itemsCenter, gap: 1, lineHeight: '18px', marginTop: '8px' }}>
            <PercentageChange value={percentage} /> last 30 days
          </Box>
        </div>
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      )}
    </Box>
  );
};

export default ThreatStatus;
